import React, { useEffect, useRef, useState, useMemo, ReactNode } from 'react';
import styled from 'styled-components';

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
`;

interface Props {
  defaultIsVisible?: boolean;
  observerOptions?: IntersectionObserverInit;
  render: ({ isVisible: boolean }) => ReactNode;
}

// IntersectionObserverのoptionの説明
// root: 交差する親要素。nullの場合はデフォルトのviewportが指定される。例えばコンポーネント内スクロールの場合に指定する
// rootMargin: 要素が現れる何px前後でイベントを起こすかの設定
// threshold: 交差領域が何%のタイミングでイベントを発火させるのかの設定
const defaultObserverOptions = { rootMargin: '40px', threshold: 0 };

// render propsで実装
const RenderLazyLoad = ({
  defaultIsVisible = false,
  observerOptions: observerOptionsProps,
  render,
}: Props) => {
  const observerOptions = {
    ...defaultObserverOptions,
    ...observerOptionsProps,
  };
  // IntersectionObserver APIが使える場合のみ遅延読み込みを実行する
  const canUseIntersectionObserver = useMemo(
    () =>
      typeof window !== 'undefined' &&
      'IntersectionObserver' in window &&
      'isIntersecting' in window.IntersectionObserverEntry.prototype,
    [],
  );

  const [isVisible, setIsVisible] = useState(
    !canUseIntersectionObserver || defaultIsVisible,
  );
  const divRef = useRef(null);

  const callback = (entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    });
  };

  // TODO コンポーネントの外で管理することで、遅延読み込み対象のコンポーネント分インスタンスが
  // 作成される状況を解決したい
  const observerRef = useRef(
    canUseIntersectionObserver
      ? new IntersectionObserver(callback, observerOptions)
      : null,
  );

  useEffect(() => {
    const element = divRef.current;
    const observer = observerRef.current;
    if (element && observer) {
      observer.observe(element);
    }
    return () => {
      const element = divRef.current;
      if (element && observer) {
        observer.unobserve(element);
      }
    };
  });

  return <Wrapper ref={divRef}>{render({ isVisible })}</Wrapper>;
};

export default RenderLazyLoad;
